import React, { useCallback, useMemo, useRef, useState } from 'react';

const toYYYYMMDD = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const parseYYYYMMDD = (s?: string) => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d);
  // Guard invalid dates (e.g. 2025-02-31)
  return date.getFullYear() === y &&
    date.getMonth() === m - 1 &&
    date.getDate() === d
    ? date
    : null;
};

const clampDate = (date: Date, min?: string, max?: string) => {
  const d = date.getTime();
  const minT = min ? (parseYYYYMMDD(min)?.getTime() ?? d) : d;
  const maxT = max ? (parseYYYYMMDD(max)?.getTime() ?? d) : d;
  return new Date(Math.min(Math.max(d, minT), maxT));
};

const addDays = (date: Date, days: number) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const addMonths = (date: Date, months: number) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  // handle month overflow (e.g., adding 1 month to Jan 31)
  if (d.getDate() !== date.getDate()) d.setDate(0);
  return d;
};

const addYears = (date: Date, years: number) => {
  const d = new Date(date);
  d.setFullYear(d.getFullYear() + years);
  // handle Feb 29 -> Feb 28 on non-leap years
  if (d.getMonth() !== date.getMonth()) d.setDate(0);
  return d;
};

export type UseDateInputOptions = {
  value?: string; // YYYY-MM-DD
  min?: string; // YYYY-MM-DD
  max?: string; // YYYY-MM-DD
  required?: boolean;
  onChange?: (v: string) => void;
  onBlur?: (v: string) => void;
};

export function useDateInput(opts: UseDateInputOptions) {
  const { value, min, max, required, onChange, onBlur } = opts;
  const todayStr = useMemo(() => toYYYYMMDD(new Date()), []);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [touched, setTouched] = useState(false);
  const [internal, setInternal] = useState<string>(value ?? '');

  // keep local state in sync with external value
  if (value !== undefined && value !== internal) {
    // avoid infinite loop

    setInternal(value);
  }

  const currentDate = useMemo(() => parseYYYYMMDD(internal), [internal]);

  const error = useMemo(() => {
    if (!touched && !required) return undefined;
    if (required && !internal) return 'This field is required.';
    if (internal && !currentDate) return 'Invalid date.';
    if (internal && min && currentDate && currentDate < parseYYYYMMDD(min)!) {
      return `Date must be on or after ${min}.`;
    }
    if (internal && max && currentDate && currentDate > parseYYYYMMDD(max)!) {
      return `Date must be on or before ${max}.`;
    }
    return undefined;
  }, [internal, currentDate, min, max, required, touched]);

  const commit = useCallback(
    (nextStr: string) => {
      setInternal(nextStr);
      onChange?.(nextStr);
    },
    [onChange]
  );

  const setToday = useCallback(() => {
    const base = clampDate(new Date(), min, max);
    commit(toYYYYMMDD(base));
  }, [commit, min, max]);

  const clear = useCallback(() => {
    commit('');
  }, [commit]);

  const nudge = useCallback(
    (unit: 'day' | 'month' | 'year', delta: number) => {
      const base = currentDate ?? new Date();
      const nudged =
        unit === 'day'
          ? addDays(base, delta)
          : unit === 'month'
            ? addMonths(base, delta)
            : addYears(base, delta);
      const clamped = clampDate(nudged, min, max);
      commit(toYYYYMMDD(clamped));
    },
    [currentDate, min, max, commit]
  );

  const handleBlur = useCallback(() => {
    setTouched(true);
    onBlur?.(internal);
  }, [internal, onBlur]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Up/Down = ±1 day
      // Alt+Up/Down = ±1 month
      // Shift+Up/Down = ±1 year
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        const sign = e.key === 'ArrowUp' ? 1 : -1;
        if (e.shiftKey) nudge('year', sign);
        else if (e.altKey) nudge('month', sign);
        else nudge('day', sign);
      }
    },
    [nudge]
  );

  return {
    ref: inputRef,
    value: internal,
    setValue: commit,
    min,
    max,
    todayStr,
    error,
    setToday,
    clear,
    handleBlur,
    handleKeyDown
  };
}
